/**
 * SQLite Status Indicator Component
 * 
 * Shows the sync status of the local SQLite database
 */

import { useState, useEffect } from 'react';
import { Database, RefreshCw, CheckCircle, AlertCircle, Loader2, WifiOff } from 'lucide-react';
import { useSQLiteContext, useSQLiteReady } from '@/contexts/SQLiteContext';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export function SQLiteStatusIndicator() {
  const isReady = useSQLiteReady();
  const { syncStats, getDatabaseSizeKB, isLoading, error } = useSQLiteContext();
  const { isOnline, isSyncing, fullSync, lastSyncAt, pendingCount } = useSQLiteSync();
  const [dbSize, setDbSize] = useState(0);

  // Update db size periodically
  useEffect(() => {
    if (!isReady) return;
    
    setDbSize(getDatabaseSizeKB());
    const interval = setInterval(() => {
      setDbSize(getDatabaseSizeKB());
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isReady, getDatabaseSizeKB]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>Iniciando...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-destructive/10 text-destructive text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>Erro no banco local</span>
      </div>
    );
  }

  const hasPending = pendingCount > 0;
  const statusColor = !isOnline ? 'text-yellow-600' : hasPending ? 'text-blue-600' : 'text-green-600';
  const bgColor = !isOnline ? 'bg-yellow-50' : hasPending ? 'bg-blue-50' : 'bg-green-50';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className={cn('gap-2 h-8', bgColor, statusColor)}
        >
          {!isOnline ? (
            <WifiOff className="h-4 w-4" />
          ) : isSyncing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : hasPending ? (
            <Database className="h-4 w-4" />
          ) : (
            <CheckCircle className="h-4 w-4" />
          )}
          {hasPending && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {pendingCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Banco de Dados Local</h4>
            <Badge variant={isReady ? 'default' : 'secondary'}>
              {isReady ? 'Ativo' : 'Inativo'}
            </Badge>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Conexão:</span>
              <span className={isOnline ? 'text-green-600' : 'text-yellow-600'}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Pendentes:</span>
              <span className={pendingCount > 0 ? 'text-blue-600 font-medium' : ''}>
                {pendingCount} operação{pendingCount !== 1 ? 'ões' : ''}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Tamanho:</span>
              <span>{dbSize} KB</span>
            </div>

            {lastSyncAt && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Última sync:</span>
                <span>{lastSyncAt.toLocaleTimeString('pt-BR')}</span>
              </div>
            )}
          </div>

          {syncStats.failed > 0 && (
            <div className="p-2 bg-destructive/10 text-destructive text-sm rounded">
              {syncStats.failed} operação(ões) falharam
            </div>
          )}

          <Button 
            onClick={fullSync} 
            disabled={isSyncing || !isOnline}
            className="w-full"
            size="sm"
          >
            {isSyncing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sincronizar Agora
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            SQLite WASM • Funciona 100% offline
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
